import React, { FC } from 'react';

import { Button, Flex, Form, Layout, message, Row, Space, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { NSKeys } from '@app/i18n';
import { LimitOwnerType } from '@app/models/products';
import { getFormErrorsFromResponse } from '@app/services/getFormErrorsFromResponse';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { HeaderTitle } from '@components/HeaderTitle';
import { ListItem } from '@components/ListItem';
import { Paper } from '@components/Paper';

import { IFormValues, ISelectedProductLimit } from '../../../../components/LimitForm';
import { ProductsTable } from '../../../../components/LimitForm/components/ProductsTable';
import { useGetCatalogLimitQuery, useGetCatalogQuery, useUpdateCatalogLimitQuery } from '../../../../hooks';

export const Edit: FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<IFormValues>();
  const breadcrumbsT = useTranslation(NSKeys.breadcrumbs);
  const catalogsT = useTranslation(NSKeys.catalogs);
  const commonT = useTranslation(NSKeys.common);
  const validationT = useTranslation(NSKeys.validation);
  const { id, limitId } = useParams<{ id: string; limitId: string }>();
  const [selectedProducts, setSelectedProducts] = React.useState<ISelectedProductLimit[]>([]);
  const [isSelectionInitialized, setIsSelectionInitialized] = React.useState(false);

  const { data: catalog, isLoading: isCatalogLoading } = useGetCatalogQuery(id || '');
  const { data: limit, isLoading: isLimitLoading } = useGetCatalogLimitQuery(id || '', limitId || '');
  const { mutate: updateLimit, status, error, reset } = useUpdateCatalogLimitQuery();

  const goBack = () => navigate(`/dashboard/catalogs/${id}/limits`);

  React.useEffect(() => {
    if (status === 'success') {
      goBack();
    }
    if (status === 'error' && error) {
      const formValues = form.getFieldsValue();
      const { pointerErrors } = getFormErrorsFromResponse(formValues, error, validationT.t, true);

      message.error(pointerErrors.length ? pointerErrors?.[0]?.errors?.join(', ') : commonT.t('somethingWentWrong'));
      reset();
    }
  }, [commonT, error, form, goBack, reset, status, validationT]);

  React.useEffect(() => {
    setIsSelectionInitialized(false);
  }, [limitId]);

  React.useEffect(() => {
    if (!limit) {
      return;
    }
    setSelectedProducts(
      (limit.items || []).map((item) => ({
        product_uid: item.product_uid,
        limit_quantity: item.limit_quantity,
      }))
    );
    setIsSelectionInitialized(true);
  }, [limit]);

  const handleSubmit = () => {
    if (!limitId) {
      return;
    }
    if (selectedProducts.length === 0) {
      return message.error(validationT.t('limit_products_required'));
    }

    updateLimit({
      limitUid: limitId,
      items: {
        items: selectedProducts.map((item) => ({
          product_uid: item.product_uid,
          limit_quantity: item.limit_quantity,
        })),
      },
    });
  };

  if (isCatalogLoading || isLimitLoading || !limit || !isSelectionInitialized) {
    return (
      <Flex style={{ width: '100%', minHeight: '400px' }} justify="center" align="center">
        <Spin />
      </Flex>
    );
  }

  return (
    <Layout>
      <Layout.Header>
        <Breadcrumbs apiTitles={[{ id: ':catalogId', value: catalog?.name }]} />
        <Row justify="space-between" align="middle">
          <HeaderTitle
            title={breadcrumbsT.t('editing')}
            onClick={goBack}
            loading={isCatalogLoading || isLimitLoading}
          />
          <Space size="middle">
            <Button
              type="primary"
              size="middle"
              form="editCatalogLimitForm"
              htmlType="submit"
              disabled={status === 'loading'}>
              {commonT.t('buttonSave')}
            </Button>
          </Space>
        </Row>
      </Layout.Header>

      <Paper style={{ margin: '24px', padding: '24px' }}>
        <Form form={form} id="editCatalogLimitForm" onFinish={handleSubmit}>
          <div style={{ maxWidth: '400px', marginBottom: 40 }}>
            <ListItem
              title={catalogsT.t('limits.ownerTypeLabel')}
              description={
                limit.limit_owner_type === LimitOwnerType.UserGroup
                  ? catalogsT.t('limits.ownerTypeGroup')
                  : catalogsT.t('limits.ownerTypeUser')
              }
            />
            <ListItem
              title={
                limit.limit_owner_type === LimitOwnerType.UserGroup
                  ? catalogsT.t('limits.ownerGroupLabel')
                  : catalogsT.t('limits.ownerUserLabel')
              }
              description={limit.limit_owner_name}
            />
          </div>

          <Typography.Title level={5} style={{ margin: 0 }}>
            {catalogsT.t('limits.productsTableHint')}
          </Typography.Title>

          {catalog?.uid && (
            <ProductsTable
              catalog_uid={catalog.uid}
              onSelectionChange={setSelectedProducts}
              selectedProductUids={selectedProducts}
            />
          )}
        </Form>
      </Paper>
    </Layout>
  );
};
